import { isNegative, isPositive, subtract, zero, } from '@aeris/money';
/**
 * Where a purchase can go, and what happens when it does.
 *
 * The same shape as the sales transition table (ADR-0017) and deliberately a
 * separate one: buying is not selling with the arrow reversed. A sale leaves
 * in one step; a delivery arrives in as many parts as the supplier feels like
 * sending.
 */
export const PURCHASE_STATUSES = [
    'draft',
    'confirmed',
    'received',
    'cancelled',
];
export const isPurchaseStatus = (value) => PURCHASE_STATUSES.includes(value);
/**
 * The legal moves, in one table.
 *
 * `received` is terminal. A delivery that turns out to be wrong is a RETURN —
 * a new document with its own movements — not an edit of the receipt, for the
 * same reason a cancelled sale reverses with new movements rather than
 * deleting the old ones: section 14 wants a ledger somebody can audit, and a
 * correction that erases its own evidence is not auditable.
 */
export const TRANSITIONS = {
    draft: ['confirmed', 'cancelled'],
    confirmed: ['received', 'cancelled'],
    received: [],
    cancelled: [],
};
export const allowedTransitions = (from) => TRANSITIONS[from];
export const canTransition = (from, to) => TRANSITIONS[from].includes(to);
/** Lines may only be edited while nobody has committed to buying them. */
export const acceptsLineEdits = (status) => status === 'draft';
export const transition = (from, to, context) => {
    if (!canTransition(from, to)) {
        return { ok: false, failure: { reason: 'illegal-transition' } };
    }
    if (to === 'confirmed' && context.lineCount === 0) {
        return { ok: false, failure: { reason: 'no-lines' } };
    }
    if (to === 'received') {
        const arriving = context.receiving ?? [];
        if (!arriving.some((line) => isPositive(line.receivingNow))) {
            return { ok: false, failure: { reason: 'nothing-received' } };
        }
    }
    return {
        ok: true,
        to,
        effect: {
            allocatesNumber: to === 'confirmed',
            writesStock: to === 'received',
        },
    };
};
/**
 * How much of this line may still arrive.
 *
 * Clamped at zero rather than allowed to go negative: a supplier who sent more
 * than was ordered is a conversation, not an arithmetic result, and the
 * receipt refuses the excess through `checkReceipt` below.
 */
export const outstanding = (line) => {
    const remaining = subtract(line.quantity, line.receivedQuantity);
    return isNegative(remaining) ? zero(remaining.scale) : remaining;
};
/**
 * Whether this delivery is one the document can accept.
 *
 * Over-receiving is refused rather than silently allowed. The tempting
 * alternative — accept it and let the stock be right — hides a real event: a
 * supplier sent more than was agreed, and somebody has to decide whether that
 * is a gift, an error or an invoice about to arrive for the difference.
 */
export const checkReceipt = (lines) => {
    let fullyReceived = true;
    for (const [index, line] of lines.entries()) {
        if (isNegative(line.receivingNow)) {
            return { ok: false, failure: { reason: 'negative-quantity', index } };
        }
        // What would still be owed after this delivery. Below zero means the
        // supplier sent more than was ordered.
        const after = subtract(outstanding(line), line.receivingNow);
        if (isNegative(after)) {
            return { ok: false, failure: { reason: 'exceeds-ordered', index } };
        }
        if (isPositive(after))
            fullyReceived = false;
    }
    return { ok: true, fullyReceived };
};
//# sourceMappingURL=transitions.js.map